import { useState, useRef, useEffect, cloneElement, createElement } from 'react';

/**
 * Takes a number and returns an array counting up to that number.
 * @param amount number
 * @returns number[]
 */
var count = function (amount) {
    var rangeArr = [];
    for (var i = 0; i < amount; i++) {
        rangeArr.push(i);
    }
    return rangeArr;
};

var id = 0;
var genId = function () { return ++id; };
var useId = function (componentName) {
    if (componentName === void 0) { componentName = '_'; }
    var uniqueId = useState(genId())[0];
    return componentName + "-" + uniqueId;
};

var isFirefox = function () { return typeof navigator !== 'undefined' && navigator.userAgent.indexOf("Firefox") !== -1; };

var isInBrowser = function () { return !!(typeof window !== 'undefined' && window.document && window.document.createElement); };

/**
 * Asserts the type of a variable by checking if the given property exists on the given variable
 * @param varToBeChecked Variable to assert type of
 * @param propertyToCheckFor Function checks for the existence of this property
 * @return boolean, but also asserts type for the TS compiler
 */
var isOfType = function (varToBeChecked, propertyToCheckFor) {
    return varToBeChecked[propertyToCheckFor] !== undefined;
};

/**
 * Checks if a point is within a Client Rect
 * @param clientRect the clientRect to check the point against
 * @param x the horizontal coordinate to check
 * @param y the vertical coordinate to check
 * @returns boolean
 */
var isPointInClientRect = function (clientRect, x, y) {
    var top = clientRect.top, left = clientRect.left, right = clientRect.right, bottom = clientRect.bottom;
    return top < y && y < bottom && left < x && x < right;
};

var joinClasses = function () {
    var classes = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        classes[_i] = arguments[_i];
    }
    return classes.filter(function (c) { return Boolean(c); }).join(' ');
};

/**
 * Takes a pixel value and returns the correct rem value for it.
 * @param pxValue number of pixels
 * @param rootFontSize the pixel count of the root font size
 * @returns string of rem value
 */
var pxToRem = function (pxValue, rootFontSize) { return String(pxValue / rootFontSize) + "rem"; };

/**
 * Creates an array with numbers ranging from <from> to <to>
 * @param from number to start from
 * @param to number to end before
 * @returns an array of numbers
 */
var range = function (from, to) {
    var rangeArr = [];
    for (var i = from; i < to; i++) {
        rangeArr.push(i);
    }
    return rangeArr;
};

var stripRem = function (remValue) { return remValue.replace(/rem/g, ''); };
/**
 * Takes a rem value and returns the correct px value for it.
 * @param remValue number of rem
 * @param rootFontSize numeric pixel count of the root font size
 * @returns string of px value
 */
var remToPx = function (remValue, rootFontSize) { return Number(stripRem(remValue)) * rootFontSize + "px"; };

/**
 * Takes a string and capitalizes it.
 * @param text the string to transform
 * @returns the transformed string
 */
var capitalizeText = function (text) { return "" + text.charAt(0).toUpperCase() + text.slice(1); };
/**
 * Takes a kebab-case string and returns a PascalCase string.
 * @param name the string to transform
 * @returns the transformed string
 */
var kebabToPascalCase = function (name) {
    return name
        .split('-')
        .map(capitalizeText)
        .join('');
};
/**
 * Takes a camelCase string and returns a kebab-case string.
 * @param name the string to transform
 * @returns the transformed string
 */
var camelToKebab = function (name) {
    return name
        .replace(/_/g, '-') // in the case of numbers, e.g. --color-grey-20 === colorGrey_20
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .toLowerCase();
};
/**
 * Takes a camelCase string and returns it in --css-variable-name format.
 * @param name the string to transform
 * @returns the transformed string
 */
var camelCaseToCssVarName = function (name) { return "--" + camelToKebab(name); };

var useInterval = function (callback, interval) {
    // tslint:disable-next-line
    var savedCallback = useRef(function () { });
    // Remember the latest callback.
    useEffect(function () {
        savedCallback.current = callback;
    }, [callback]);
    // Set up the interval.
    useEffect(function () {
        var tick = function () { return savedCallback.current(); };
        var id = setInterval(tick, interval);
        return function () { return clearInterval(id); };
    }, [interval]);
};

/**
 * Clones an element and applies an additional class name to it.
 * @param element - the element to clone
 * @param classNames - the class names to apply
 */
var cloneElementWithClassname = function (element) {
    var classNames = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        classNames[_i - 1] = arguments[_i];
    }
    return cloneElement(element, { className: joinClasses.apply(void 0, [element.props.className].concat(classNames)) });
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

/**
 * Takes in a map of properties that contain arrays of optional values for a given key
 * and combines those values into a properties object which a test is then run on.
 * @param propOptions - an object showing the different value for different props in order
 * @param testFn - the test function to run ton the props
 * @param testProps - a privately used accumulator
 */
var runTestOnPropOptions = function (propOptions, testFn, testProps) {
    if (!propOptions || Object.keys(propOptions).length === 0) {
        return propOptions;
    }
    var key = Object.keys(propOptions)[0];
    var _a = key, currentPropValues = propOptions[_a], remainingProps = __rest(propOptions, [typeof _a === "symbol" ? _a : _a + ""]);
    // TODO: Use TS generic for util so we don't have to use 'any' below
    var currentProps;
    currentPropValues.forEach(function (value) {
        var _a;
        currentProps = __assign({}, testProps, (_a = {}, _a[key] = value, _a));
        describe("when " + key + " is " + value, function () {
            var nextProps = runTestOnPropOptions(remainingProps, testFn, currentProps);
            if (nextProps && Object.keys(nextProps).length === 0) {
                testFn(currentProps);
            }
        });
    });
};

var HTMLRenderer = function (_a) {
    var html = _a.html;
    return createElement("div", { dangerouslySetInnerHTML: { __html: html } });
};

var bundle_es = /*#__PURE__*/Object.freeze({
    __proto__: null,
    HTMLRenderer: HTMLRenderer,
    camelCaseToCssVarName: camelCaseToCssVarName,
    camelToKebab: camelToKebab,
    capitalizeText: capitalizeText,
    cloneElementWithClassname: cloneElementWithClassname,
    count: count,
    genId: genId,
    isFirefox: isFirefox,
    isInBrowser: isInBrowser,
    isOfType: isOfType,
    isPointInClientRect: isPointInClientRect,
    joinClasses: joinClasses,
    kebabToPascalCase: kebabToPascalCase,
    pxToRem: pxToRem,
    range: range,
    remToPx: remToPx,
    runTestOnPropOptions: runTestOnPropOptions,
    useId: useId,
    useInterval: useInterval
});

export { bundle_es as b, joinClasses as j };
